import type { CSSProperties } from 'react';

const styles: Record<string, CSSProperties> = {
  container: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: 'var(--color-text)',
    margin: 0,
  },
  subtitle: {
    color: 'var(--color-text-secondary)',
    marginTop: '0.25rem',
  },
  card: {
    background: 'var(--color-surface, white)',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  cardTitle: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 600,
    color: 'var(--color-text)',
    margin: 0,
  },
  input: {
    flex: 1,
    padding: '0.75rem 1rem',
    fontSize: 'var(--text-xl)',
    border: '1px solid var(--color-border, #e1e5e9)',
    borderRadius: 'var(--radius)',
    outline: 'none',
  },
  select: {
    padding: '0.75rem 1rem',
    fontSize: 'var(--text-xl)',
    border: '1px solid var(--color-border, #e1e5e9)',
    borderRadius: 'var(--radius)',
    outline: 'none',
    minWidth: '180px',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: 'var(--text-xl)',
    border: '1px solid var(--color-border, #e1e5e9)',
    borderRadius: 'var(--radius)',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  label: {
    display: 'block',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    marginBottom: '0.5rem',
    color: 'var(--color-text)',
  },
  primaryBtn: {
    padding: '0.75rem 1.5rem',
    background: 'var(--color-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  greenBtn: {
    padding: '0.75rem 1.5rem',
    background: 'var(--color-success)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: 600,
    cursor: 'pointer',
  },
  grayBtn: {
    padding: '0.75rem 1.5rem',
    background: 'var(--color-text-light)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: 600,
    cursor: 'pointer',
  },
  smallBtn: {
    padding: '0.375rem 0.75rem',
    background: 'var(--color-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  errorBox: {
    background: 'var(--color-error-bg)',
    color: 'var(--color-error)',
    padding: '1rem',
    borderRadius: 'var(--radius)',
    marginBottom: '1rem',
    border: '1px solid #fecaca',
  },
  successBox: {
    background: 'var(--color-success-bg)',
    color: 'var(--color-success)',
    padding: '1rem',
    borderRadius: 'var(--radius)',
    marginBottom: '1rem',
    border: '1px solid #bbf7d0',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '3px solid #e5e7eb',
    borderTopColor: 'var(--color-primary, #3b82f6)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
  statusBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    color: 'white',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  smallBadge: {
    padding: '0.125rem 0.5rem',
    borderRadius: '9999px',
    color: 'white',
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
  },
  detailGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailLabel: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-secondary, #6b7280)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  detailValue: {
    fontSize: 'var(--text-xl)',
    fontWeight: 500,
    color: 'var(--color-text)',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid var(--color-border, #e1e5e9)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontSize: 'var(--text-base)',
    fontWeight: 600,
  },
  td: {
    padding: '0.5rem',
    borderBottom: '1px solid var(--color-border, #e1e5e9)',
    color: 'var(--color-text)',
  },
  tableRow: {
    transition: 'background 0.15s',
  },
  tableInput: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--color-border, #e1e5e9)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-base)',
    boxSizing: 'border-box',
  },
  removeBtn: {
    width: '28px',
    height: '28px',
    background: 'transparent',
    color: 'var(--color-error)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-4xl)',
    fontWeight: 700,
    cursor: 'pointer',
    lineHeight: 1,
  },
  previewCard: {
    background: '#fefce8',
    border: '2px solid #fde047',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  previewTitle: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 600,
    color: '#854d0e',
    marginBottom: '1rem',
  },
  previewSubtitle: {
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: '0.5rem',
  },
  emailOption: {
    marginTop: '1.5rem',
    padding: '1rem',
    background: 'var(--color-info-bg)',
    border: '1px solid #bfdbfe',
    borderRadius: 'var(--radius)',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    marginTop: '2px',
  },
  dangerCard: {
    background: 'var(--color-error-bg)',
    border: '2px solid #fecaca',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  dangerTitle: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 600,
    color: 'var(--color-error)',
    marginBottom: '0.5rem',
  },
  dangerText: {
    color: 'var(--color-error-text)',
    fontSize: 'var(--text-base)',
    marginBottom: '1rem',
  },
  dangerSection: {
    background: 'white',
    padding: '1rem',
    borderRadius: 'var(--radius)',
  },
  dangerSectionTitle: {
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    color: 'var(--color-error-text)',
    marginBottom: '0.5rem',
  },
  dangerSectionText: {
    fontSize: 'var(--text-base)',
    color: 'var(--color-text-light)',
    marginBottom: '0.75rem',
  },
  dangerBtn: {
    width: '100%',
    padding: '0.75rem 1rem',
    background: 'var(--color-error)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: 600,
    cursor: 'pointer',
  },
  infoBox: {
    background: 'var(--color-info-bg)',
    border: '1px solid #bfdbfe',
    borderRadius: 'var(--radius)',
    padding: '1rem',
  },
  infoText: {
    fontSize: 'var(--text-base)',
    color: 'var(--color-info-text)',
    marginBottom: '0.5rem',
  },
  infoTextSmall: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-info)',
    marginBottom: '0.25rem',
    lineHeight: 1.4,
  },
};

export default styles;

// Add spinner animation
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}
